import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Check,
  BookOpen,
  Clock,
  FileText,
  Video,
  HelpCircle,
  Trophy,
  Menu,
  X,
  FolderOpen,
} from 'lucide-react';
import CourseDocuments from '../../components/CourseDocuments';
import { enrollmentsApi, EnrollmentData, activitiesApi } from '../../lib/api';
import { formatMinutes, cn } from '../../lib/utils';
import { SectionLoader, InlineLoader } from '../../components/ui/PremiumLoader';
import toast from 'react-hot-toast';
import { useAnalytics, AnalyticsEvents } from '../../hooks/useAnalytics';

// Activity tracking constants
const ACTIVITY_SYNC_INTERVAL = 60 * 1000; // Sync every 60 seconds
const MIN_ACTIVITY_DURATION = 10; // Minimum 10 seconds before counting as activity

interface Lesson {
  id: string;
  title: string;
  type: 'VIDEO' | 'DOCUMENT' | 'QUIZ';
  duration?: number;
  contentUrl?: string;
  isFree: boolean;
  progress?: {
    isCompleted: boolean;
    watchTime: number;
  } | null;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  lessons: Lesson[];
}

interface CourseWithSections {
  id: string;
  title: string;
  slug: string;
  thumbnailUrl?: string;
  totalLessons: number;
  sections: Section[];
}

interface EnrollmentWithProgress extends Omit<EnrollmentData, 'course'> {
  course: CourseWithSections;
}

interface ProgressData {
  enrollmentId: string;
  overallProgress: number;
  status: string;
  sections: Section[];
}

export default function CoursePlayerPage() {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const navigate = useNavigate();
  const { trackEvent, trackLessonProgress } = useAnalytics();
  const [enrollment, setEnrollment] = useState<EnrollmentWithProgress | null>(null);
  const [progress, setProgress] = useState<ProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'lessons' | 'documents'>('lessons');
  const lastTrackedMilestone = useRef<number>(0);

  // Activity tracking refs
  const activityStartTimeRef = useRef<number>(Date.now());
  const accumulatedSecondsRef = useRef<number>(0);
  const lastSyncTimeRef = useRef<number>(Date.now());
  const isActiveRef = useRef<boolean>(true);
  const courseIdRef = useRef<string | undefined>(undefined);

  // Sync accumulated activity to the server
  const syncActivity = useCallback(async (forceSync = false) => {
    const now = Date.now();
    const sessionSeconds = Math.floor((now - activityStartTimeRef.current) / 1000);
    const totalSeconds = accumulatedSecondsRef.current + sessionSeconds;
    
    // Only sync if we have accumulated at least MIN_ACTIVITY_DURATION seconds
    if (totalSeconds < MIN_ACTIVITY_DURATION && !forceSync) return;
    
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 1 && !forceSync) return; // Need at least 1 minute to record
    
    try {
      await activitiesApi.record({
        minutes: Math.max(1, minutes), // At least 1 minute
        courseId: courseIdRef.current,
      });
      
      // Reset tracking after successful sync
      accumulatedSecondsRef.current = totalSeconds % 60; // Keep remaining seconds
      activityStartTimeRef.current = now;
      lastSyncTimeRef.current = now;
    } catch (error) {
      console.error('Failed to sync activity:', error);
      // Keep accumulated time on failure for next sync attempt
      accumulatedSecondsRef.current = totalSeconds;
      activityStartTimeRef.current = now;
    }
  }, []);

  // Handle visibility change (user switching tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User left the tab - accumulate time and pause tracking
        const now = Date.now();
        const sessionSeconds = Math.floor((now - activityStartTimeRef.current) / 1000);
        accumulatedSecondsRef.current += sessionSeconds;
        isActiveRef.current = false;
      } else {
        // User returned - resume tracking
        activityStartTimeRef.current = Date.now();
        isActiveRef.current = true;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Set up periodic activity sync
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (isActiveRef.current && enrollmentId) {
        syncActivity();
      }
    }, ACTIVITY_SYNC_INTERVAL);

    return () => clearInterval(intervalId);
  }, [enrollmentId, syncActivity]);

  // Sync activity when component unmounts or enrollmentId changes
  useEffect(() => {
    // Reset tracking when entering a new course
    activityStartTimeRef.current = Date.now();
    accumulatedSecondsRef.current = 0;
    
    return () => {
      // Sync remaining activity when leaving
      syncActivity(true);
    };
  }, [enrollmentId, syncActivity]);

  // Update courseId ref when enrollment changes
  useEffect(() => {
    if (enrollment?.course?.id) {
      courseIdRef.current = enrollment.course.id;
    }
  }, [enrollment?.course?.id]);

  // Fetch enrollment and progress
  useEffect(() => {
    const fetchData = async () => {
      if (!enrollmentId) return;
      
      setIsLoading(true);
      try {
        const [enrollmentRes, progressRes] = await Promise.all([
          enrollmentsApi.getById(enrollmentId),
          enrollmentsApi.getProgress(enrollmentId),
        ]);

        if (enrollmentRes.data.success) {
          const enrollmentData = enrollmentRes.data.data as unknown as EnrollmentWithProgress;
          setEnrollment(enrollmentData);
          
          // Expand first section by default
          if (enrollmentData.course.sections.length > 0) {
            setExpandedSections([enrollmentData.course.sections[0].id]);
          }
        }

        if (progressRes.data.success) {
          const progressData = progressRes.data.data as ProgressData;
          setProgress(progressData);
          
          // Find and set current lesson (first incomplete or first lesson)
          const firstIncomplete = findFirstIncompleteLesson(progressData.sections);
          if (firstIncomplete) {
            setCurrentLesson(firstIncomplete);
          } else if (progressData.sections[0]?.lessons[0]) {
            setCurrentLesson(progressData.sections[0].lessons[0]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch course data:', error);
        toast.error('Failed to load course');
        navigate('/student/my-courses');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [enrollmentId, navigate]);

  const findFirstIncompleteLesson = (sections: Section[]): Lesson | null => {
    for (const section of sections) {
      for (const lesson of section.lessons) {
        if (!lesson.progress?.isCompleted) {
          return lesson;
        }
      }
    }
    return null;
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleLessonClick = (lesson: Lesson) => {
    // Track lesson started
    if (enrollment?.course?.id && !lesson.progress?.isCompleted) {
      trackLessonProgress('started', lesson.id, lesson.title, lesson.type, enrollment.course.id);
    }
    setCurrentLesson(lesson);
    setIsMobileSidebarOpen(false);
  };

  const markAsComplete = useCallback(async () => {
    if (!enrollmentId || !currentLesson || isUpdatingProgress) return;

    setIsUpdatingProgress(true);
    try {
      const response = await enrollmentsApi.updateProgress(enrollmentId, {
        lessonId: currentLesson.id,
        isCompleted: true,
        watchTime: currentLesson.duration || 0,
      });

      if (response.data.success) {
        // Track lesson completion
        if (enrollment?.course?.id) {
          trackLessonProgress('completed', currentLesson.id, currentLesson.title, currentLesson.type, enrollment.course.id);
        }
        
        // Update local state
        setProgress((prev) => {
          if (!prev) return prev;
          
          const updatedSections = prev.sections.map((section) => ({
            ...section,
            lessons: section.lessons.map((lesson) =>
              lesson.id === currentLesson.id
                ? {
                    ...lesson,
                    progress: { isCompleted: true, watchTime: currentLesson.duration || 0 },
                  }
                : lesson
            ),
          }));

          // Calculate new progress
          const totalLessons = updatedSections.reduce(
            (sum, s) => sum + s.lessons.length,
            0
          );
          const completedLessons = updatedSections.reduce(
            (sum, s) =>
              sum + s.lessons.filter((l) => l.progress?.isCompleted).length,
            0
          );
          const newProgress = (completedLessons / totalLessons) * 100;
          
          // Track progress milestones (25%, 50%, 75%)
          const milestones = [25, 50, 75];
          for (const milestone of milestones) {
            if (newProgress >= milestone && lastTrackedMilestone.current < milestone) {
              trackEvent(AnalyticsEvents.COURSE_PROGRESS_MILESTONE, {
                courseId: enrollment?.course?.id,
                courseTitle: enrollment?.course?.title,
                milestone,
              });
              lastTrackedMilestone.current = milestone;
            }
          }
          
          // Track course completion
          if (newProgress >= 100 && prev.overallProgress < 100) {
            trackEvent(AnalyticsEvents.COURSE_COMPLETED, {
              courseId: enrollment?.course?.id,
              courseTitle: enrollment?.course?.title,
              totalLessons,
            });
          }

          return {
            ...prev,
            overallProgress: newProgress,
            sections: updatedSections,
            status: newProgress >= 100 ? 'COMPLETED' : 'ACTIVE',
          };
        });

        // Auto-advance to next lesson
        const nextLesson = getNextLesson();
        if (nextLesson) {
          toast.success('Lesson completed! Moving to next...');
          setTimeout(() => setCurrentLesson(nextLesson), 1000);
        } else {
          toast.success('🎉 Congratulations! You completed the course!');
        }
      }
    } catch (error) {
      console.error('Failed to update progress:', error);
      toast.error('Failed to mark as complete');
    } finally {
      setIsUpdatingProgress(false);
    }
  }, [enrollmentId, currentLesson, isUpdatingProgress]);

  const getNextLesson = (): Lesson | null => {
    if (!progress || !currentLesson) return null;

    let foundCurrent = false;
    for (const section of progress.sections) {
      for (const lesson of section.lessons) {
        if (foundCurrent) {
          return lesson;
        }
        if (lesson.id === currentLesson.id) {
          foundCurrent = true;
        }
      }
    }
    return null;
  };

  const getPreviousLesson = (): Lesson | null => {
    if (!progress || !currentLesson) return null;

    let previousLesson: Lesson | null = null;
    for (const section of progress.sections) {
      for (const lesson of section.lessons) {
        if (lesson.id === currentLesson.id) {
          return previousLesson;
        }
        previousLesson = lesson;
      }
    }
    return null;
  };

  const getLessonIcon = (type: string) => {
    switch (type) {
      case 'VIDEO':
        return Video;
      case 'DOCUMENT':
        return FileText;
      case 'QUIZ':
        return HelpCircle;
      default:
        return BookOpen;
    }
  };

  if (isLoading) {
    return <SectionLoader className="h-full" text="Loading course..." />;
  }

  if (!enrollment || !progress) {
    return null;
  }

  const isCompleted = currentLesson?.progress?.isCompleted;
  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();
  const overallProgress = Number(progress.overallProgress);

  return (
    <div className="h-full flex flex-col -m-6 -mt-4">
      {/* Top Header */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          background: 'rgba(0,0,0,0.3)',
          borderBottom: '1px solid rgba(255,255,255,0.04)',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
          >
            <Menu className="w-5 h-5 text-white/60" />
          </button>
          <Link
            to="/student/my-courses"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Courses</span>
          </Link>
        </div>

        <div className="flex-1 mx-4 max-w-xl">
          <h1 className="text-sm font-medium text-white truncate">
            {enrollment.course.title}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.5 }}
                className="h-full rounded-full"
                style={{
                  background:
                    overallProgress >= 100
                      ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
                      : 'linear-gradient(90deg, #dc464a 0%, #f87171 100%)',
                }}
              />
            </div>
            <span className="text-xs text-white/40 whitespace-nowrap">
              {Math.round(overallProgress)}% complete
            </span>
          </div>
        </div>

        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg 
                   text-white/60 hover:text-white hover:bg-white/[0.06] transition-colors"
        >
          <BookOpen className="w-4 h-4" />
          <span className="text-sm">Content</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex min-h-0">
        {/* Video/Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Content Display */}
          <div className="flex-1 flex items-center justify-center bg-black/20">
            {currentLesson ? (
              <div className="w-full h-full flex flex-col items-center justify-center p-8">
                {currentLesson.type === 'VIDEO' && currentLesson.contentUrl ? (
                  <video
                    src={currentLesson.contentUrl}
                    controls
                    className="max-w-full max-h-full rounded-xl"
                    style={{ maxHeight: 'calc(100vh - 300px)' }}
                  />
                ) : (
                  <div className="text-center max-w-md">
                    <div
                      className="w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center"
                      style={{ background: 'rgba(220, 70, 74, 0.15)' }}
                    >
                      {(() => {
                        const Icon = getLessonIcon(currentLesson.type);
                        return <Icon className="w-10 h-10 text-coral" />;
                      })()}
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">
                      {currentLesson.title}
                    </h2>
                    <p className="text-white/40 mb-6">
                      {currentLesson.type === 'DOCUMENT'
                        ? 'Document content will be displayed here'
                        : currentLesson.type === 'QUIZ'
                        ? 'Quiz will be available here'
                        : 'Content coming soon'}
                    </p>
                    {currentLesson.duration && (
                      <p className="text-white/30 text-sm flex items-center justify-center gap-2">
                        <Clock className="w-4 h-4" />
                        {formatMinutes(currentLesson.duration / 60)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <BookOpen className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Select a lesson to begin</p>
              </div>
            )}
          </div>

          {/* Lesson Controls */}
          {currentLesson && (
            <div
              className="flex items-center justify-between p-4 flex-shrink-0"
              style={{
                background: 'rgba(0,0,0,0.2)',
                borderTop: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => previousLesson && setCurrentLesson(previousLesson)}
                  disabled={!previousLesson}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl 
                           text-white/60 hover:text-white hover:bg-white/[0.06]
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Previous</span>
                </button>

                <button
                  onClick={() => nextLesson && setCurrentLesson(nextLesson)}
                  disabled={!nextLesson}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl 
                           text-white/60 hover:text-white hover:bg-white/[0.06]
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <h3 className="text-white font-medium hidden md:block truncate max-w-[300px]">
                  {currentLesson.title}
                </h3>
              </div>

              <button
                onClick={markAsComplete}
                disabled={isCompleted || isUpdatingProgress}
                className={cn(
                  'flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all',
                  isCompleted
                    ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
                    : 'bg-gradient-to-r from-coral to-coral/80 text-white hover:opacity-90'
                )}
              >
                {isUpdatingProgress ? (
                  <>
                    <InlineLoader />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : isCompleted ? (
                  <>
                    <Check className="w-4 h-4" />
                    Completed
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Mark Complete
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Sidebar - Desktop */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 360, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="hidden lg:flex flex-col border-l border-white/[0.04] overflow-hidden flex-shrink-0"
              style={{ background: 'rgba(0,0,0,0.2)' }}
            >
              {/* Sidebar Tabs */}
              <div className="flex gap-1 p-2 border-b border-white/[0.04]">
                <button
                  onClick={() => setSidebarTab('lessons')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                    sidebarTab === 'lessons'
                      ? 'bg-coral/20 text-coral'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  )}
                >
                  <BookOpen className="w-4 h-4" />
                  Lessons
                </button>
                <button
                  onClick={() => setSidebarTab('documents')}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
                    sidebarTab === 'documents'
                      ? 'bg-coral/20 text-coral'
                      : 'text-white/50 hover:text-white hover:bg-white/5'
                  )}
                >
                  <FolderOpen className="w-4 h-4" />
                  Documents
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {sidebarTab === 'documents' ? (
                  <div className="p-4">
                    <CourseDocuments courseId={enrollment.course.id} hasAccess={true} />
                  </div>
                ) : (
                  <>
                {progress.sections.map((section) => (
                  <div key={section.id}>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center justify-between p-4 
                               hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 text-white/40 transition-transform',
                            expandedSections.includes(section.id) && 'rotate-180'
                          )}
                        />
                        <span className="font-medium text-white text-sm text-left">
                          {section.title}
                        </span>
                      </div>
                      <span className="text-xs text-white/30">
                        {section.lessons.filter((l) => l.progress?.isCompleted).length}/
                        {section.lessons.length}
                      </span>
                    </button>

                    <AnimatePresence>
                      {expandedSections.includes(section.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          {section.lessons.map((lesson) => {
                            const LessonIcon = getLessonIcon(lesson.type);
                            const isCurrent = currentLesson?.id === lesson.id;
                            const isLessonCompleted = lesson.progress?.isCompleted;

                            return (
                              <button
                                key={lesson.id}
                                onClick={() => handleLessonClick(lesson)}
                                className={cn(
                                  'w-full flex items-center gap-3 px-4 py-3 pl-10',
                                  'hover:bg-white/[0.02] transition-colors text-left',
                                  isCurrent && 'bg-coral/10 border-l-2 border-coral'
                                )}
                              >
                                <div
                                  className={cn(
                                    'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                                    isLessonCompleted
                                      ? 'bg-emerald-500/20'
                                      : 'bg-white/[0.04]'
                                  )}
                                >
                                  {isLessonCompleted ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                  ) : (
                                    <LessonIcon className="w-3.5 h-3.5 text-white/40" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p
                                    className={cn(
                                      'text-sm truncate',
                                      isCurrent ? 'text-coral' : 'text-white/70'
                                    )}
                                  >
                                    {lesson.title}
                                  </p>
                                  {lesson.duration && (
                                    <p className="text-xs text-white/30 mt-0.5">
                                      {formatMinutes(lesson.duration / 60)}
                                    </p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
                  </>
                )}
              </div>

              {/* Course Completed Banner */}
              {overallProgress >= 100 && (
                <div className="p-4 border-t border-white/[0.04]">
                  <div
                    className="p-4 rounded-xl text-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                    }}
                  >
                    <Trophy className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                    <p className="font-medium text-emerald-400">Course Completed!</p>
                    <p className="text-xs text-white/40 mt-1">
                      Congratulations on finishing the course
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Sidebar Overlay */}
        <AnimatePresence>
          {isMobileSidebarOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween', duration: 0.2 }}
                className="fixed left-0 top-0 bottom-0 w-80 z-50 lg:hidden flex flex-col"
                style={{
                  background: 'linear-gradient(180deg, #18181f 0%, #111116 100%)',
                  borderRight: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
                  <h2 className="font-semibold text-white">Course Content</h2>
                  <button
                    onClick={() => setIsMobileSidebarOpen(false)}
                    className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
                  >
                    <X className="w-5 h-5 text-white/60" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {progress.sections.map((section) => (
                    <div key={section.id}>
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="w-full flex items-center justify-between p-4 
                                 hover:bg-white/[0.02] transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronDown
                            className={cn(
                              'w-4 h-4 text-white/40 transition-transform',
                              expandedSections.includes(section.id) && 'rotate-180'
                            )}
                          />
                          <span className="font-medium text-white text-sm text-left">
                            {section.title}
                          </span>
                        </div>
                      </button>

                      {expandedSections.includes(section.id) && (
                        <div>
                          {section.lessons.map((lesson) => {
                            const LessonIcon = getLessonIcon(lesson.type);
                            const isCurrent = currentLesson?.id === lesson.id;
                            const isLessonCompleted = lesson.progress?.isCompleted;

                            return (
                              <button
                                key={lesson.id}
                                onClick={() => handleLessonClick(lesson)}
                                className={cn(
                                  'w-full flex items-center gap-3 px-4 py-3 pl-10',
                                  'hover:bg-white/[0.02] transition-colors text-left',
                                  isCurrent && 'bg-coral/10 border-l-2 border-coral'
                                )}
                              >
                                <div
                                  className={cn(
                                    'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                                    isLessonCompleted
                                      ? 'bg-emerald-500/20'
                                      : 'bg-white/[0.04]'
                                  )}
                                >
                                  {isLessonCompleted ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                                  ) : (
                                    <LessonIcon className="w-3.5 h-3.5 text-white/40" />
                                  )}
                                </div>
                                <p
                                  className={cn(
                                    'text-sm truncate flex-1',
                                    isCurrent ? 'text-coral' : 'text-white/70'
                                  )}
                                >
                                  {lesson.title}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
